import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { formatUserHandle } from "../../utils/userHandle";
import {
  useGetCurrentSeasonQuery,
  useGetGameReachQuery,
  useGetSeasonRosterQuery,
  useGetSeasonStandingsQuery,
} from "./api/gameApi";
import type { Season, Standings } from "./api/types";
import SeasonHeader from "./components/SeasonHeader";
import Countdown from "./components/Countdown";
import PointsTable from "./components/PointsTable";
import SpecialDays from "./components/SpecialDays";
import PrizeCard from "./components/PrizeCard";
import RosterList from "./components/RosterList";
import StandingsTable from "./components/StandingsTable";
import ReachWarning from "./components/ReachWarning";
import JoinFlow from "./join/JoinFlow";
import { fmtDayWithWeekday, fmtInstantDay, multiplierText } from "./utils/seasonText";
import "./game.css";

type Flow = null | "join" | "fix";

function RulesPanel({ season }: { season: Season }) {
  return (
    <section className="gm-panel" aria-label="Points">
      <h2 className="gm-panel__title">Points per race</h2>
      <PointsTable points={season.points} />
      {season.rulesText ? <p>{season.rulesText}</p> : null}
      <p className="gm-muted">
        {season.countDaysBeforeJoin
          ? "Late joiners keep their earlier races."
          : "Points start with the first race after you join."}
      </p>
    </section>
  );
}

function JoinCta({
  season,
  signedIn,
  onJoin,
}: {
  season: Season;
  signedIn: boolean;
  onJoin: () => void;
}) {
  if (season.me?.joined) {
    return (
      <p className="gm-banner gm-banner--mint" data-testid="joined-note">
        You&apos;re in.
      </p>
    );
  }
  // before joining opens nobody can join, signed in or not
  if (season.phase === "announced") {
    return (
      <p className="gm-muted">
        {season.opensAt ? `Joining opens on ${fmtInstantDay(season.opensAt)}.` : "Joining opens soon."}
      </p>
    );
  }
  if (!season.joinOpen) return <p className="gm-muted">Joining is closed.</p>;
  if (!signedIn) {
    return (
      <div className="gm-actions">
        <Link to={`/login?return=${encodeURIComponent("/game")}`} className="gm-button">
          Sign in to join
        </Link>
      </div>
    );
  }
  const lateLine =
    season.phase === "active"
      ? season.countDaysBeforeJoin
        ? "You can still join. Your earlier races count."
        : "You can still join. Your points start with the next race."
      : null;
  return (
    <div className="gm-panel gm-panel--mint">
      {lateLine ? <p>{lateLine}</p> : null}
      <button type="button" className="gm-button gm-button--block" onClick={onJoin}>
        Join the game
      </button>
    </div>
  );
}

function standingsNote(standings: Standings | undefined): string | null {
  if (!standings) return null;
  if (!standings.asOfDayKey) return "No race is counted yet.";
  const after = `After day ${standings.asOfDayNumber ?? standings.settledDays}.`;
  return standings.final ? after : `${after} Today's guessing is not counted yet.`;
}

/**
 * /game: the 2026 season on the morning race. Three states from the season's
 * phase: teaser (announced, open), active, ended. Join happens in place
 * through JoinFlow.
 */
const GamePage: React.FC = () => {
  const auth = useAppSelector((s) => s.auth);
  const ready = auth.bootstrapStatus === "ready";
  const signedIn = ready && !!auth.token;

  // wait for the auth bootstrap so the request carries the token (and `me`)
  const {
    data: season,
    isLoading,
    isUninitialized,
    isError,
    refetch,
  } = useGetCurrentSeasonQuery(undefined, { skip: !ready });
  const id = season?.id;
  const { data: roster } = useGetSeasonRosterQuery(id ?? "", { skip: !id });
  const { data: standings, isLoading: standingsLoading } = useGetSeasonStandingsQuery(id ?? "", {
    skip: !id || season?.phase === "announced" || season?.phase === "open",
  });

  const joined = !!season?.me?.joined;
  const watchReach = signedIn && joined && (season?.phase === "open" || season?.phase === "active");
  const {
    data: reach,
    fulfilledTimeStamp: reachAt,
    refetch: refetchReach,
    isError: reachFailed,
  } = useGetGameReachQuery(undefined, { skip: !watchReach });

  const [flow, setFlow] = useState<Flow>(null);
  // The fix flow checks reach through its own (fresh) requests, so this page's
  // answer is stale when it closes. Hide the warning until a newer answer lands
  // (a failed refresh shows it again).
  const [staleReachAt, setStaleReachAt] = useState<number | null>(null);
  const reachIsStale = staleReachAt !== null && (reachAt ?? 0) === staleReachAt && !reachFailed;

  const closeFlow = () => {
    if (flow === "fix" && watchReach) {
      setStaleReachAt(reachAt ?? 0);
      try {
        void refetchReach();
      } catch {
        // skipped query: nothing to refresh
      }
    }
    setFlow(null);
  };

  let body: React.ReactNode;
  if (isLoading || isUninitialized) {
    body = <p className="gm-muted">Loading the game...</p>;
  } else if (isError) {
    body = (
      <div className="gm-panel">
        <p>Could not load the game. Try again in a minute.</p>
        <div className="gm-actions">
          <button type="button" className="gm-button gm-button--plain" onClick={() => refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  } else if (!season) {
    body = (
      <header className="gm-head">
        <p className="gm-kicker">The game</p>
        <h1 className="gm-title">No game is running right now.</h1>
      </header>
    );
  } else {
    const joinPanel =
      flow && season ? (
        <JoinFlow
          season={season}
          mode={flow}
          onClose={closeFlow}
        />
      ) : null;
    const reachWarning =
      watchReach && reach && !reach.reachable && !reachIsStale && flow !== "fix" ? (
        <ReachWarning onFix={() => setFlow("fix")} />
      ) : null;
    const cta = flow ? null : (
      <JoinCta season={season} signedIn={signedIn} onJoin={() => setFlow("join")} />
    );

    if (season.phase === "announced" || season.phase === "open") {
      body = (
        <>
          <SeasonHeader season={season}>
            <Countdown target={season.startsAt} />
            <p className="gm-muted">Starts with the race on {fmtDayWithWeekday(season.dayKeyFrom)}.</p>
          </SeasonHeader>
          {reachWarning}
          {cta}
          {joinPanel}
          <PrizeCard prizeText={season.prizeText} />
          <RulesPanel season={season} />
          <SpecialDays days={season.specialDays} />
          <RosterList roster={roster} />
        </>
      );
    } else if (season.phase === "active") {
      const dayLine =
        season.daysLeft <= 1
          ? `Day ${season.dayNumber ?? season.totalDays} of ${season.totalDays}, last day.`
          : `Day ${season.dayNumber ?? 1} of ${season.totalDays}, ${season.daysLeft} days left.`;
      body = (
        <>
          <SeasonHeader season={season}>
            <p className="gm-lead">
              <strong>{dayLine}</strong>
            </p>
          </SeasonHeader>
          {season.todaySpecial ? (
            <p className="gm-banner">
              Today{season.todaySpecial.label ? ` is ${season.todaySpecial.label}` : ""}:{" "}
              {multiplierText(season.todaySpecial.multiplier)}.
            </p>
          ) : null}
          {reachWarning}
          {cta}
          {joinPanel}
          <section className="gm-step" aria-label="Standings">
            <h2 className="gm-panel__title">Standings</h2>
            {standingsLoading ? (
              <p className="gm-muted">Loading the standings...</p>
            ) : (
              <StandingsTable rows={standings?.rows ?? []} frames={standings?.frames} />
            )}
            {standingsNote(standings) ? <p className="gm-muted">{standingsNote(standings)}</p> : null}
          </section>
          <details className="gm-details gm-panel">
            <summary>How it works</summary>
            <div>
              <PointsTable points={season.points} />
              {season.rulesText ? <p>{season.rulesText}</p> : null}
              <p className="gm-muted">
                {season.countDaysBeforeJoin
                  ? "Late joiners keep their earlier races."
                  : "Points start with the first race after you join."}
              </p>
              <SpecialDays days={season.specialDays} />
              <PrizeCard prizeText={season.prizeText} />
            </div>
          </details>
        </>
      );
    } else {
      const rows = standings?.rows ?? [];
      const winners = rows.filter((r) => r.rank === 1);
      const finalDone = season.lastFinalDayKey === season.dayKeyTo;
      body = (
        <>
          <header className="gm-head">
            <p className="gm-kicker">{season.name}</p>
            <h1 className="gm-title">The game is over.</h1>
          </header>
          {winners.length > 0 ? (
            <section className="gm-panel gm-panel--lemon" aria-label="Winner">
              <h2 className="gm-panel__title">
                {winners.length === 1 ? "Winner" : "Shared win"}
              </h2>
              <p data-testid="winner-line">
                {winners.map((w) => formatUserHandle(w.username, w.emoji)).join(" and ")} with{" "}
                {winners[0].total} points.
              </p>
              {season.prizeText ? <p>{season.prizeText}</p> : null}
            </section>
          ) : null}
          <section className="gm-step" aria-label="Final standings">
            <h2 className="gm-panel__title">Final standings</h2>
            {standingsLoading ? (
              <p className="gm-muted">Loading the standings...</p>
            ) : (
              <StandingsTable rows={rows} frames={standings?.frames} />
            )}
            <p className="gm-muted">
              {finalDone ? "Results are final." : "The last day is still being counted."}
            </p>
          </section>
        </>
      );
    }
  }

  return (
    <div className="gm-page">
      <div className="gm-inner">{body}</div>
    </div>
  );
};

export default GamePage;
